import { t } from "i18next";
import { ReactElement, useState } from "react";
import { PageHeader } from "../../../UIPalette/components/PageHeader/PageHeader";
import {
  useDeleteWisdomUserFunctionRequest,
  useEraseUserFunctionRequest,
  useListWisdomUsersFunctionRequest,
  WisdomUserResponseModel,
} from "shared/request/myHealthyAdvantageApi";
import { ListWisdomUsersOrderBy } from "./ListWisdomUsersOrderBy";
import { OrderByDirection, RoleKeys } from "core/Constants";
import { RowMenuItem, Table, TableHeader, TableOrdering, TablePaging } from "../../../UIPalette/components/Table/Table";
import { useDebounce } from "shared/core/hooks/useDebounce";
import { ClientDivisionSelection, ClientDivisionSelector } from "../../../UIPalette/components/ClientDivisionSelector/ClientDivisionSelector";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import { Input } from "shared/UI/Input/Input";
import { ReactComponent as SearchIcon } from "@icons/magnifying-glass.svg";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

export const ViewWisdomUsers = (): ReactElement => {
  const { isAuthorised, user } = useAdminUserContext();
  const { modalNotify } = useNotifyModalContext();
  const [clientDivisionFilter, setClientDivisionFilter] = useState<ClientDivisionSelection>();
  const [searchFilter, setSearchFilter] = useState<string>();
  const debouncedSearchFilter = useDebounce(searchFilter, 500);
  const [paging, setPaging] = useState<TablePaging>({ current: 1, size: 10 });
  const [ordering, setOrdering] = useState<TableOrdering>({
    column: ListWisdomUsersOrderBy.CreatedDateUtc,
    direction: OrderByDirection.Descending,
  });
  const [userToDelete, setUserToDelete] = useState<WisdomUserResponseModel>();
  const [userToErase, setUserToErase] = useState<WisdomUserResponseModel>();
  const { trigger: deleteUser } = useDeleteWisdomUserFunctionRequest();
  const { trigger: eraseUser } = useEraseUserFunctionRequest();
  const { data: usersResponse, mutate } = useListWisdomUsersFunctionRequest({
    page: paging.current,
    perPage: paging.size,
    orderBy: ordering.column,
    orderByDirection: ordering.direction,
    keyword: debouncedSearchFilter,
    clientId: clientDivisionFilter?.client?.id,
    divisionId: clientDivisionFilter?.division?.id,
  });

  const tableHeaders: TableHeader[] = [
    { text: t("userManagement.table.nameHeading"), orderByValue: ListWisdomUsersOrderBy.Forenames },
    {
      text: t("userManagement.table.surnameHeading"),
      orderByValue: ListWisdomUsersOrderBy.Surname,
    },
    ...(!user?.client
      ? [
        {
          text: t("userManagement.table.clientHeading"),
          orderByValue: ListWisdomUsersOrderBy.Client,
        },
      ]
      : []),
    ...(!user?.division
      ? [
        {
          text: t("userManagement.table.divisionHeading"),
          orderByValue: ListWisdomUsersOrderBy.Division,
        },
      ]
      : []),
    { text: t("userManagement.table.emailHeading"), orderByValue: ListWisdomUsersOrderBy.Email },
    {
      text: t("userManagement.table.creationDateHeading"),
      orderByValue: ListWisdomUsersOrderBy.CreatedDateUtc,
    },
  ];

  const mapTableData = () =>
    usersResponse?.records?.map(({ forenames, surname, email, client, division, createdDateUtc }) => {
      return {
        forenames,
        surname,
        ...(!user?.client && { client: client?.name }),
        ...(!user?.division && { division: division?.name }),
        email,
        createdDate: String.formatDateTime(createdDateUtc),
      };
    });

  const buildRowMenuItems = (): RowMenuItem[] =>
    [
      isAuthorised({ permissions: ["delete-users"] }) && {
        title: t("table.deleteTitle"),
        "aria-label": t("table.ariaDeleteRow"),
        onClick: (row: number) => setUserToDelete(getUserFromRow(row)),
      },
      isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["erase-users"] }) && {
        title: t("table.eraseTitle"),
        "aria-label": t("table.ariaEraseRow"),
        onClick: (row: number) => setUserToErase(getUserFromRow(row)),
      },
    ].filter(Boolean) as RowMenuItem[];

  const onEraseUser = async () => {
    try {
      await eraseUser({ body: { email: userToErase!.email } });
      await mutate();
      modalNotify("success", t("eraseModal.successfulTitle"), t("eraseModal.successfulErase"));
    } catch {
      modalNotify("error", t("eraseModal.failedTitle"), t("eraseModal.failedErase"), t("eraseModal.failedCta"));
    } finally {
      setUserToErase(undefined);
    }
  };

  const onDeleteUser = async () => {
    try {
      await deleteUser({ query: { id: userToDelete!.id } });
      await mutate();
      modalNotify("success", t("userManagement.successTitle"), t("userManagement.successfulDelete"));
    } catch {
      modalNotify("error", t("userManagement.failedTitle"), t("userManagement.failedDelete"), t("userManagement.failedCta"));
    } finally {
      setUserToDelete(undefined);
    }
  };

  const getUserFromRow = (row: number) => usersResponse?.records?.at(row);

  return (
    <>
      <PageHeader title={t("userManagement.navBarTitle")} subtitle={t("userManagement.subtitle")} />
      <Hideable hidden={!isAuthorised({ role: RoleKeys.HaAdmin })}>
        <ClientDivisionSelector onSelection={(clientDivisionFilters) => setClientDivisionFilter(clientDivisionFilters)} />
      </Hideable>
      <div className="w-2/5 my-2 content-center">
        <Input
          aria-label={t("userManagement.searchInputLabel")}
          onChange={(a) => setSearchFilter(a.target.value)}
          icon={<SearchIcon />}
          placeholder={t("userManagement.searchPlaceholder")}
          label={t("userManagement.searchLabel")}
        />
      </div>
      <Table
        headers={tableHeaders}
        dataRows={mapTableData() ?? []}
        rowMenuItems={buildRowMenuItems()}
        orderedByColumn={ordering.column}
        orderedDirection={ordering.direction}
        currentPage={paging.current}
        pageSize={paging.size}
        totalDataCount={usersResponse?.metadata?.totalCount ?? 0}
        onOrderingChange={setOrdering}
        onPagingChange={setPaging}
      />
      {userToDelete && (
        <ConfirmationModal
          title={t("userManagement.deleteModal.title")}
          message={t("userManagement.deleteModal.message", { userEmail: userToDelete?.email })}
          cancelButtonText={t("userManagement.deleteModal.cancel")}
          confirmButtonText={t("userManagement.deleteModal.confirm")}
          onCancel={() => setUserToDelete(undefined)}
          onConfirm={onDeleteUser}
        />
      )}
      {userToErase && (
        <ConfirmationModal
          title={t("eraseModal.title")}
          message={t("eraseModal.message", { userEmail: userToErase?.email })}
          cancelButtonText={t("eraseModal.cancel")}
          confirmButtonText={t("eraseModal.confirm")}
          onCancel={() => setUserToErase(undefined)}
          onConfirm={onEraseUser}
        />
      )}
    </>
  );
};
